import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { hourlyPackagesApi, hourlyPackagesQueryKeys } from '../api';
import type { PackageFilters, CreateHourlyPackageDto, UpdateHourlyPackageDto, BaseStatus } from '../types';

export function useHourlyPackagesList(filters?: PackageFilters) {
  return useQuery({
    queryKey: hourlyPackagesQueryKeys.list(filters),
    queryFn: () => hourlyPackagesApi.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useHourlyPackage(id: number | string | undefined) {
  return useQuery({
    queryKey: hourlyPackagesQueryKeys.detail(id ?? ''),
    queryFn: () => hourlyPackagesApi.getById(id!),
    enabled: !!id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
}

export function useHourlyPackageMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateHourlyPackageDto) => hourlyPackagesApi.create(data),
    onSuccess: () => {
      toast.success(t('hourlyPackageCreatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: hourlyPackagesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingHourlyPackage')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateHourlyPackageDto }) =>
      hourlyPackagesApi.update({ id, data }),
    onSuccess: (_, { id }) => {
      toast.success(t('hourlyPackageUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: hourlyPackagesQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: hourlyPackagesQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingHourlyPackage')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number | string) => hourlyPackagesApi.delete(id),
    onSuccess: () => {
      toast.success(t('hourlyPackageDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: hourlyPackagesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingHourlyPackage')));
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number | string; status: BaseStatus }) =>
      hourlyPackagesApi.changeStatus({ id, status }),
    onSuccess: (_, { id }) => {
      toast.success(t('hourlyPackageUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: hourlyPackagesQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: hourlyPackagesQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingHourlyPackage')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    changeStatusMutation,
  };
}
